import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES, parsePhone, composePhone } from "@/lib/countries";

/**
 * International phone input with searchable country dropdown (flag + dial code).
 * `value` and `onChange` deal with the composed E.164 string (e.g. "+14155551234").
 */
export default function PhoneInput({
  value = "",
  onChange,
  placeholder = "555 123 4567",
  disabled = false,
  inputClassName = "",
  testIdPrefix = "phone",
}) {
  const parsed = useMemo(() => parsePhone(value), [value]);
  const [country, setCountry] = useState(parsed.country);
  const [national, setNational] = useState(parsed.national);
  const [open, setOpen] = useState(false);

  // If the caller's stored value changes externally (e.g. after refresh), keep in sync,
  // but ignore round-trips of the value we just emitted (which would clobber the user's
  // chosen country when several countries share a dial code, e.g. +1 for US/CA).
  const externalKey = value || "";
  const [lastExternal, setLastExternal] = useState(externalKey);
  if (externalKey !== lastExternal) {
    setLastExternal(externalKey);
    const currentComposed = composePhone(country, national);
    if (externalKey !== currentComposed) {
      setCountry(parsed.country);
      setNational(parsed.national);
    }
  }

  const emit = (nextCountry, nextNational) => {
    if (onChange) onChange(composePhone(nextCountry, nextNational));
  };

  const pickCountry = (code) => {
    const next = COUNTRIES.find((c) => c.code === code) || country;
    setCountry(next);
    setOpen(false);
    emit(next, national);
  };

  const onNationalChange = (raw) => {
    const cleaned = raw.replace(/[^0-9\s\-().]/g, "");
    setNational(cleaned);
    emit(country, cleaned);
  };

  return (
    <div className="flex items-stretch gap-2" data-testid={`${testIdPrefix}-wrapper`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testIdPrefix}-country-trigger`}
            className="inline-flex items-center gap-1.5 px-2.5 h-10 min-w-[104px] bg-zinc-950 border border-zinc-800 rounded-sm font-mono text-sm text-zinc-200 hover:border-zinc-700 disabled:opacity-50"
          >
            <span className="text-lg leading-none">{country.flag}</span>
            <span className="text-xs">{country.dial}</span>
            <ChevronsUpDown className="w-3 h-3 text-zinc-500 ml-auto" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[280px] p-0 bg-[#0a0a0a] border-zinc-800 rounded-sm">
          <Command className="bg-transparent">
            <CommandInput placeholder="Search country…" data-testid={`${testIdPrefix}-country-search`} className="h-9 font-mono text-xs" />
            <CommandList className="max-h-[280px]">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.dial} ${c.code}`}
                    onSelect={() => pickCountry(c.code)}
                    data-testid={`${testIdPrefix}-country-option-${c.code}`}
                    className="text-xs font-mono cursor-pointer"
                  >
                    <span className="text-base mr-2">{c.flag}</span>
                    <span className="flex-1 text-zinc-200 truncate">{c.name}</span>
                    <span className="text-zinc-500 ml-2">{c.dial}</span>
                    {c.code === country.code && <Check className="w-3.5 h-3.5 text-blue-400 ml-2" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder={placeholder}
        value={national}
        onChange={(e) => onNationalChange(e.target.value)}
        disabled={disabled}
        data-testid={`${testIdPrefix}-national-input`}
        className={`bg-zinc-950 border-zinc-800 rounded-sm font-mono h-10 flex-1 ${inputClassName}`}
      />
    </div>
  );
}
